import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CopyRequestMenu from "@/components/CopyRequestMenu";
import EnvManager, { loadEnv, substituteDeep } from "@/components/EnvManager";
import {
  Loader2, Play, Copy, RefreshCw, CheckCircle2, XCircle, Clock, FlaskConical, History,
  Plus, Trash2, ChevronRight, ArrowRight, Save, Workflow, Zap, Settings,
} from "lucide-react";

const PROVIDER_LABELS = {
  manarecharge_bbps: "ManaRecharge · BBPS",
  manarecharge_payout: "ManaRecharge · Payout",
  manarecharge_pg: "ManaRecharge · PG",
  razorpay: "Razorpay",
  cashfree: "Cashfree",
  runpaisa: "Runpaisa",
  msg91: "MSG91 SMS",
  hitch_zone: "Hitch.zone (Parent system)",
  default_gateway: "Default gateway",
};

export default function AdminApiTester() {
  const [catalog, setCatalog] = useState([]);
  const [endpointId, setEndpointId] = useState("");
  const [params, setParams] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);  // { ok, response, error, elapsed_ms, ... }
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Postman-like environment (variables) ──
  const [env, setEnv] = useState(() => loadEnv());
  const [envOpen, setEnvOpen] = useState(false);

  const endpoint = useMemo(() => catalog.find((e) => e.id === endpointId), [catalog, endpointId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/api-tester/catalog");
        setCatalog(data.endpoints || []);
        if (data.endpoints?.[0]) setEndpointId(data.endpoints[0].id);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load catalog");
      }
    })();
  }, []);

  // Reset params when endpoint changes
  useEffect(() => { setParams({}); setResult(null); }, [endpointId]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/admin/api-tester/log", { params: { limit: 30 } });
      setHistory(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load history");
    } finally { setLoadingHistory(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const run = async () => {
    if (!endpoint) return;
    setRunning(true);
    setResult(null);
    try {
      const interpolated = substituteDeep(params, env);
      // Warn (but don't block) if unresolved tokens remain after substitution
      const unresolved = JSON.stringify(interpolated).match(/\{\{[\w.]+\}\}/g);
      if (unresolved && unresolved.length) {
        toast.warning(`Unresolved variable(s): ${[...new Set(unresolved)].join(", ")}`);
      }
      const { data } = await api.post("/admin/api-tester/run", {
        endpoint_id: endpoint.id,
        params: interpolated,
      });
      setResult(data);
      if (data.ok) toast.success(`Provider responded in ${data.elapsed_ms}ms`);
      else toast.error(data.error || "Provider call failed");
      loadHistory();
    } catch (err) {
      setResult({ ok: false, error: formatApiError(err.response?.data?.detail) || "Request failed", elapsed_ms: null, response: err.response?.data });
      toast.error(formatApiError(err.response?.data?.detail) || "Request failed");
    } finally { setRunning(false); }
  };

  const copyJSON = (obj) => {
    try { navigator.clipboard.writeText(JSON.stringify(obj, null, 2)); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  const replayFromHistory = (h) => {
    setEndpointId(h.endpoint_id);
    setTimeout(() => setParams(h.params || {}), 50);
  };

  // Group catalog by provider for the picker
  const groupedCatalog = useMemo(() => {
    const groups = {};
    catalog.forEach((e) => {
      const key = e.provider || "other";
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    });
    return groups;
  }, [catalog]);

  // ── Save-as-flow-step (capture current single-test request into a flow) ──
  const [saveFlowOpen, setSaveFlowOpen] = useState(false);
  const [flows, setFlows] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [saveFlowMode, setSaveFlowMode] = useState("new"); // "new" | "existing"
  const [saveFlowTargetId, setSaveFlowTargetId] = useState("");
  const [saveFlowName, setSaveFlowName] = useState("");
  const [saveFlowStepLabel, setSaveFlowStepLabel] = useState("");
  const [savingFlow, setSavingFlow] = useState(false);

  const openSaveFlowDialog = async () => {
    if (!endpoint) return;
    setSaveFlowOpen(true);
    setSaveFlowStepLabel(endpoint.label || "");
    setFlowsLoading(true);
    try {
      const { data } = await api.get("/admin/api-tester/flows");
      const custom = (data.flows || []).filter((f) => !f.is_prebuilt);
      setFlows(custom);
      if (custom.length > 0) {
        setSaveFlowMode("existing");
        setSaveFlowTargetId(custom[0].id);
      } else {
        setSaveFlowMode("new");
        setSaveFlowName(`Flow · ${endpoint.label}`);
      }
    } catch {
      setFlows([]);
      setSaveFlowMode("new");
      setSaveFlowName(`Flow · ${endpoint.label}`);
    } finally { setFlowsLoading(false); }
  };

  const submitSaveFlow = async () => {
    if (!endpoint) return;
    const step = {
      endpoint_id: endpoint.id,
      label: saveFlowStepLabel || endpoint.label || endpoint.id,
      params: { ...(params || {}) },
      param_mappings: {},
    };
    setSavingFlow(true);
    try {
      if (saveFlowMode === "new") {
        const name = (saveFlowName || "").trim();
        if (!name) { toast.error("Flow name required"); setSavingFlow(false); return; }
        await api.post("/admin/api-tester/flows", {
          name,
          description: `Created from single test on ${new Date().toLocaleString()}`,
          steps: [step],
        });
        toast.success(`Flow "${name}" created with this step`);
      } else {
        if (!saveFlowTargetId) { toast.error("Pick a flow to append to"); setSavingFlow(false); return; }
        const { data: flow } = await api.get(`/admin/api-tester/flows/${saveFlowTargetId}`);
        const newSteps = [...(flow.steps || []), step];
        await api.put(`/admin/api-tester/flows/${saveFlowTargetId}`, { steps: newSteps });
        toast.success(`Step appended to "${flow.name}" (now ${newSteps.length} steps)`);
      }
      setSaveFlowOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSavingFlow(false); }
  };

  return (
    <DashboardLayout>
      <div>
        <span className="overline">Admin · Diagnostics</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          External API Tester
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Run any provider call (BBPS, Payouts, PG) with arbitrary inputs and inspect the raw
          response. Useful for verifying credentials, IP whitelisting, and response shapes.
        </p>
      </div>

      <Tabs defaultValue="single" className="mt-6" data-testid="api-tester-tabs">
        <TabsList className="grid grid-cols-2 w-72">
          <TabsTrigger value="single" data-testid="tab-single"><FlaskConical className="h-3.5 w-3.5 mr-1.5" /> Single test</TabsTrigger>
          <TabsTrigger value="flows" data-testid="tab-flows"><Workflow className="h-3.5 w-3.5 mr-1.5" /> API flows</TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-4">
          <SingleTestTab
            catalog={catalog} endpointId={endpointId} setEndpointId={setEndpointId}
            params={params} setParams={setParams} endpoint={endpoint}
            groupedCatalog={groupedCatalog} running={running} result={result}
            run={run} copyJSON={copyJSON} history={history} loadHistory={loadHistory}
            loadingHistory={loadingHistory} replayFromHistory={replayFromHistory}
            onSaveAsFlowStep={openSaveFlowDialog}
            env={env} onOpenEnv={() => setEnvOpen(true)}
          />
        </TabsContent>

        <TabsContent value="flows" className="mt-4">
          <FlowsTab catalog={catalog} env={env} onOpenEnv={() => setEnvOpen(true)} />
        </TabsContent>
      </Tabs>

      {/* ─── Save-as-flow-step dialog ─── */}
      <Dialog open={saveFlowOpen} onOpenChange={setSaveFlowOpen}>
        <DialogContent className="max-w-md" data-testid="save-flow-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Save as flow step</DialogTitle>
            <DialogDescription>
              Capture this request as a reusable step in an API flow — useful for chaining successful single tests without retyping params.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {endpoint && (
              <div className="rounded-lg border bg-muted/30 px-3 py-2.5">
                <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-muted-foreground">Step to save</p>
                <p className="text-sm font-mono mt-1">{endpoint.id}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {endpoint.label} · {Object.keys(params || {}).length} param(s)
                </p>
              </div>
            )}

            <div>
              <Label className="text-xs">Step label</Label>
              <Input
                value={saveFlowStepLabel}
                onChange={(e) => setSaveFlowStepLabel(e.target.value)}
                placeholder="e.g., 1. Create PG order"
                className="mt-1"
                data-testid="step-label-input"
              />
            </div>

            <div>
              <Label className="text-xs">Save into</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={saveFlowMode === "existing" ? "default" : "outline"}
                  size="sm"
                  disabled={flows.length === 0}
                  onClick={() => setSaveFlowMode("existing")}
                  className="flex-1"
                  data-testid="mode-existing"
                >
                  Existing flow {flows.length > 0 && `(${flows.length})`}
                </Button>
                <Button
                  type="button"
                  variant={saveFlowMode === "new" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSaveFlowMode("new")}
                  className="flex-1"
                  data-testid="mode-new"
                >
                  New flow
                </Button>
              </div>
            </div>

            {saveFlowMode === "existing" ? (
              <div>
                <Label className="text-xs">Append to flow</Label>
                <Select value={saveFlowTargetId} onValueChange={setSaveFlowTargetId}>
                  <SelectTrigger className="mt-1" data-testid="flow-picker">
                    <SelectValue placeholder={flowsLoading ? "Loading…" : "Pick a flow"} />
                  </SelectTrigger>
                  <SelectContent>
                    {flows.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name} · {(f.steps || []).length} steps
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs">New flow name</Label>
                <Input
                  value={saveFlowName}
                  onChange={(e) => setSaveFlowName(e.target.value)}
                  placeholder="e.g., ManaRecharge BBPS End-to-End"
                  className="mt-1"
                  data-testid="new-flow-name"
                />
              </div>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setSaveFlowOpen(false)}>Cancel</Button>
            <Button onClick={submitSaveFlow} disabled={savingFlow} data-testid="save-flow-submit">
              {savingFlow && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {saveFlowMode === "existing" ? "Append step" : "Create flow"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Environment variables (Postman-like) ─── */}
      <EnvManager
        open={envOpen}
        onOpenChange={setEnvOpen}
        onChange={(rows) => setEnv(rows)}
      />
    </DashboardLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SingleTestTab — extracted from the original component
// ═══════════════════════════════════════════════════════════════════════════
function SingleTestTab({ catalog, endpointId, setEndpointId, params, setParams, endpoint, groupedCatalog, running, result, run, copyJSON, history, loadHistory, loadingHistory, replayFromHistory, onSaveAsFlowStep, env, onOpenEnv }) {
  const envCount = (env || []).length;
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface-card p-5 space-y-4" data-testid="api-tester-form">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs">Endpoint</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onOpenEnv}
              className="h-7 text-[11px] gap-1.5"
              data-testid="open-env-manager"
              title="Manage environment variables (use {{varname}} in any param)"
            >
              <Settings className="h-3 w-3" />
              ENV
              {envCount > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] font-mono">
                  {envCount}
                </Badge>
              )}
            </Button>
          </div>
          <div>
            <Select value={endpointId} onValueChange={setEndpointId}>
              <SelectTrigger className="mt-1" data-testid="endpoint-picker"><SelectValue placeholder="Pick an endpoint…" /></SelectTrigger>
              <SelectContent className="max-h-96">
                {Object.entries(groupedCatalog).map(([prov, items]) => (
                  <React.Fragment key={prov}>
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">{PROVIDER_LABELS[prov] || prov}</div>
                    {items.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`endpoint-${e.id}`}>{e.label}</SelectItem>)}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
            {endpoint?.description && <p className="text-[11px] text-muted-foreground mt-2">{endpoint.description}</p>}
          </div>
          {endpoint && (endpoint.params?.length > 0 ? (
            <div className="space-y-3 pt-2 border-t border-border">
              {endpoint.params.map((p) => (
                <div key={p.key}>
                  <Label className="text-xs flex items-center gap-1">{p.label}{p.required && <span className="text-destructive">*</span>}<span className="text-[10px] font-mono text-muted-foreground ml-1">{p.key}</span></Label>
                  {p.type === "select" ? (
                    <Select value={params[p.key] || ""} onValueChange={(v) => setParams({ ...params, [p.key]: v })}>
                      <SelectTrigger className="mt-1" data-testid={`param-${p.key}`}><SelectValue placeholder="Pick one…" /></SelectTrigger>
                      <SelectContent>{p.options?.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input type={p.type === "number" ? "number" : "text"} value={params[p.key] || ""} onChange={(e) => setParams({ ...params, [p.key]: e.target.value })} placeholder={p.placeholder || ""} className="mt-1 font-mono text-sm" data-testid={`param-${p.key}`} />
                  )}
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-muted-foreground py-3">No parameters required for this endpoint.</p>)}
          <div className="pt-3 border-t border-border flex gap-2">
            <Button onClick={run} disabled={!endpoint || running} data-testid="run-btn">{running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}Run</Button>
            {result && <Badge variant="outline" className={result.ok ? "border-success/40 text-success bg-success/5" : "border-destructive/40 text-destructive bg-destructive/5"}>{result.ok ? "OK" : "FAIL"} · {result.elapsed_ms ?? "—"}ms</Badge>}
          </div>
        </div>
        <div className="surface-card p-5" data-testid="api-tester-history">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><History className="h-4 w-4 text-muted-foreground" /><h3 className="font-display font-semibold text-sm">Recent runs</h3></div>
            <Button variant="ghost" size="sm" onClick={loadHistory} disabled={loadingHistory}>{loadingHistory ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}</Button>
          </div>
          <div className="mt-3 space-y-2 max-h-[420px] overflow-auto">
            {history.length === 0 && !loadingHistory && <p className="text-xs text-muted-foreground py-6 text-center">No runs yet.</p>}
            {history.map((h) => (
              <button key={h.id} type="button" onClick={() => replayFromHistory(h)} className="w-full text-left rounded-md border border-border p-2.5 hover:bg-accent/40 transition-colors" data-testid={`history-${h.id}`}>
                <div className="flex items-center gap-2 text-[11px]">{h.ok ? <CheckCircle2 className="h-3 w-3 text-success" /> : <XCircle className="h-3 w-3 text-destructive" />}<span className="font-medium truncate flex-1">{h.endpoint_label}</span><span className="font-mono text-muted-foreground">{h.elapsed_ms}ms</span></div>
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">{(h.created_at || "").slice(0, 16).replace("T", " ")} · by {h.by || "—"}</div>
                {!h.ok && h.error && <div className="text-[10px] text-destructive mt-1 truncate">{h.error}</div>}
              </button>
            ))}
          </div>
        </div>
      </div>
      {result && (
        <div className="mt-6 surface-card p-5" data-testid="result-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-semibold text-sm flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Response</h3>
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-xs font-mono text-muted-foreground">{result.elapsed_ms ?? "—"}ms</span>
              <Button variant="outline" size="sm" onClick={() => copyJSON(result.response ?? result)} data-testid="copy-response"><Copy className="h-3.5 w-3.5 mr-1" /> Copy JSON</Button>
            </div>
          </div>
          {result.outbound_request && (
            <div className="mt-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center justify-between">
                <span>Outbound request</span>
                <CopyRequestMenu
                  request={result.outbound_request}
                  response={result.response}
                  onSaveAsFlowStep={onSaveAsFlowStep}
                />
              </div>
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="request-panel">
                <div className="flex items-center gap-2 text-xs font-mono">
                  <Badge variant="outline" className="border-primary/40 text-primary text-[10px] py-0 h-4">{result.outbound_request.method || "POST"}</Badge>
                  <code className="break-all flex-1">{result.outbound_request.url}</code>
                </div>
                {result.outbound_request.body && <pre className="text-[11px] font-mono bg-stone-950 text-stone-100 p-2 rounded overflow-auto">{JSON.stringify(result.outbound_request.body, null, 2)}</pre>}
              </div>
            </div>
          )}
          {result.error && (
            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3" data-testid="result-error">
              <div className="text-xs font-medium text-destructive">{result.error_type || "Error"}</div>
              <pre className="text-xs mt-1 whitespace-pre-wrap font-mono text-muted-foreground">{result.error}</pre>
            </div>
          )}
          <div className="mt-3 rounded-md bg-stone-950 text-stone-100 p-4 overflow-auto max-h-[60vh]" data-testid="result-json">
            <pre className="text-[11px] font-mono leading-relaxed">{result.response != null ? JSON.stringify(result.response, null, 2) : "// no response"}</pre>
          </div>
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FlowsTab — multi-step API flow runner
// ═══════════════════════════════════════════════════════════════════════════
function FlowsTab({ catalog, env, onOpenEnv }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [stepResults, setStepResults] = useState([]);  // [{response, ok, elapsed_ms, error}]
  const [currentStep, setCurrentStep] = useState(0);
  const [stepParams, setStepParams] = useState({});
  const [runningStep, setRunningStep] = useState(false);

  // Save flow dialog
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [selectedItems, setSelectedItems] = useState({});  // {stepIndex: selectedObject}
  const [pickerStep, setPickerStep] = useState(null);       // step index to show picker for
  const [pickerSearch, setPickerSearch] = useState("");

  const loadFlows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/api-tester/flows");
      setFlows(data.flows || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load flows");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadFlows(); }, []);

  // When an item is selected from an array response, auto-advance to next step with pre-filled params
  useEffect(() => {
    if (!selectedFlow) return;
    const lastSelectedIdx = Object.keys(selectedItems).map(Number).sort((a, b) => b - a)[0];
    if (lastSelectedIdx == null) return;
    const nextIdx = lastSelectedIdx + 1;
    if (nextIdx < selectedFlow.steps.length && stepResults[lastSelectedIdx]) {
      const nextStep = selectedFlow.steps[nextIdx];
      const ep = getEndpoint(nextStep.endpoint_id);
      const selected = selectedItems[lastSelectedIdx];
      const nextParams = {};

      // 1. Try explicit param_mappings first
      Object.entries(nextStep.param_mappings || {}).forEach(([key, mapping]) => {
        const resolved = resolveParamValue(mapping);
        if (resolved) nextParams[key] = resolved;
      });

      // 2. Smart auto-fill from selected item: match param keys to item fields
      if (selected && ep?.params) {
        for (const p of ep.params) {
          if (nextParams[p.key]) continue; // already filled by mapping
          // Direct match: param key matches item field
          if (selected[p.key] != null) { nextParams[p.key] = String(selected[p.key]); continue; }
          // Common aliases: biller_id → id, category → category, consumer_id → skip
          if (p.key === "biller_id" && selected.id) { nextParams[p.key] = String(selected.id); continue; }
          if (p.key === "operator_id" && selected.id) { nextParams[p.key] = String(selected.id); continue; }
        }
      }

      setStepParams(nextParams);
      setCurrentStep(nextIdx);
    }
  }, [selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectFlow = (flow) => {
    setSelectedFlow(flow);
    setStepResults([]);
    setCurrentStep(0);
    setStepParams({});
    setSelectedItems({});
    setPickerStep(null);
  };

  const getEndpoint = (endpointId) => catalog.find((e) => e.id === endpointId);

  // Resolve param value from previous step results, selected items, or manual input
  const resolveParamValue = (mapping) => {
    if (!mapping || mapping.source === "manual") return "";
    if (mapping.source === "step" && mapping.step_index != null) {
      // First check if there's a selected item for that step
      const selected = selectedItems[mapping.step_index];
      if (selected) {
        const path = (mapping.path || "").split(".");
        // Try to resolve from selected item directly (e.g., path = "id" or "name")
        const shortPath = path[path.length - 1]; // last segment
        if (selected[shortPath] != null) return String(selected[shortPath]);
        // Also try full path navigation through selected
        let val = selected;
        for (const p of path) {
          if (val && typeof val === "object") val = val[p];
          else { val = undefined; break; }
        }
        if (val != null) return String(val);
      }
      // Fallback: navigate from the full step result
      const prevResult = stepResults[mapping.step_index];
      if (!prevResult?.response) return "";
      const path = (mapping.path || "").split(".");
      let val = prevResult;
      for (const p of path) {
        if (val && typeof val === "object") val = val[p];
        else { val = undefined; break; }
      }
      return val != null ? String(val) : "";
    }
    return "";
  };

  const runStep = async () => {
    if (!selectedFlow) return;
    const step = selectedFlow.steps[currentStep];
    if (!step) return;
    setRunningStep(true);
    try {
      const interpolated = substituteDeep(stepParams, env);
      const unresolved = JSON.stringify(interpolated).match(/\{\{[\w.]+\}\}/g);
      if (unresolved && unresolved.length) {
        toast.warning(`Unresolved variable(s): ${[...new Set(unresolved)].join(", ")}`);
      }
      const { data } = await api.post("/admin/api-tester/run", {
        endpoint_id: step.endpoint_id,
        params: interpolated,
      });
      const newResults = [...stepResults];
      newResults[currentStep] = data;
      setStepResults(newResults);
      if (data.ok) {
        toast.success(`Step ${currentStep + 1} completed in ${data.elapsed_ms}ms`);
        // If response is an array, don't auto-advance — let admin pick an item first
        if (Array.isArray(data.response) && data.response.length > 0) {
          setPickerStep(currentStep);
          setPickerSearch("");
        } else if (currentStep < selectedFlow.steps.length - 1) {
          // Auto-advance and pre-fill next step params
          const nextStep = selectedFlow.steps[currentStep + 1];
          const nextParams = {};
          Object.entries(nextStep.param_mappings || {}).forEach(([key, mapping]) => {
            const resolved = resolveParamValue(mapping);
            if (resolved) nextParams[key] = resolved;
          });
          setStepParams(nextParams);
          setCurrentStep(currentStep + 1);
        }
      } else {
        toast.error(data.error || "Step failed");
      }
    } catch (err) {
      const errResult = { ok: false, error: formatApiError(err.response?.data?.detail) || "Request failed", elapsed_ms: null, response: err.response?.data };
      const newResults = [...stepResults];
      newResults[currentStep] = errResult;
      setStepResults(newResults);
      toast.error(errResult.error);
    } finally { setRunningStep(false); }
  };

  const saveAsFlow = async () => {
    if (!saveName.trim()) { toast.error("Name required"); return; }
    if (stepResults.length === 0) { toast.error("Run at least one step first"); return; }
    try {
      const steps = selectedFlow.steps.map((s, i) => ({
        ...s,
        params: stepResults[i]?.kwargs || {},
      }));
      await api.post("/admin/api-tester/flows", { name: saveName, description: saveDesc, steps });
      toast.success("Flow saved");
      setSaveOpen(false);
      setSaveName("");
      setSaveDesc("");
      loadFlows();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    }
  };

  const deleteFlow = async (id) => {
    if (!window.confirm("Delete this flow?")) return;
    try {
      await api.delete(`/admin/api-tester/flows/${id}`);
      toast.success("Flow deleted");
      if (selectedFlow?.id === id) setSelectedFlow(null);
      loadFlows();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left: Flow list */}
      <div className="surface-card p-5" data-testid="flow-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-sm flex items-center gap-2"><Workflow className="h-4 w-4" /> Flow templates</h3>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenEnv}
              className="h-7 text-[11px] gap-1.5"
              data-testid="open-env-manager-flows"
              title="Manage environment variables (use {{varname}} in any param)"
            >
              <Settings className="h-3 w-3" />
              ENV
              {(env || []).length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px] font-mono">{(env || []).length}</Badge>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={loadFlows}><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div className="space-y-2 max-h-[500px] overflow-auto">
          {loading && <p className="text-xs text-muted-foreground py-4 text-center">Loading...</p>}
          {flows.map((f) => (
            <button key={f.id} type="button" onClick={() => selectFlow(f)}
              className={`w-full text-left rounded-md border p-3 transition-colors ${selectedFlow?.id === f.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent/40"}`}
              data-testid={`flow-${f.id}`}>
              <div className="flex items-center gap-2">
                {f.is_prebuilt ? <Zap className="h-3.5 w-3.5 text-amber-500" /> : <Workflow className="h-3.5 w-3.5 text-muted-foreground" />}
                <span className="font-medium text-sm flex-1">{f.name}</span>
                {!f.is_prebuilt && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); deleteFlow(f.id); }} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {f.description && <p className="text-[11px] text-muted-foreground mt-1">{f.description}</p>}
              <div className="flex items-center gap-1 mt-2">
                <Badge variant="outline" className="text-[10px]">{f.steps?.length || 0} steps</Badge>
                {f.is_prebuilt && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Pre-built</Badge>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Flow runner */}
      <div className="lg:col-span-2 space-y-4">
        {!selectedFlow ? (
          <div className="surface-card p-8 text-center text-muted-foreground">
            <Workflow className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Select a flow template to start running a multi-step API journey.</p>
          </div>
        ) : (
          <>
            <div className="surface-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-semibold">{selectedFlow.name}</h3>
                  {selectedFlow.description && <p className="text-xs text-muted-foreground mt-0.5">{selectedFlow.description}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setStepResults([]); setCurrentStep(0); setStepParams({}); }}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reset
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSaveOpen(true)}>
                    <Save className="h-3.5 w-3.5 mr-1" /> Save as new
                  </Button>
                </div>
              </div>

              {/* Step indicators */}
              <div className="flex items-center gap-1 mb-4 flex-wrap">
                {selectedFlow.steps.map((s, i) => {
                  const result = stepResults[i];
                  const isCurrent = i === currentStep;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                      <button type="button" onClick={() => { setCurrentStep(i); if (!stepResults[i]) setStepParams({}); }}
                        className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                          isCurrent ? "bg-primary text-primary-foreground" :
                          result?.ok ? "bg-emerald-100 text-emerald-700" :
                          result?.error ? "bg-red-100 text-red-700" :
                          "bg-muted text-muted-foreground"
                        }`}>
                        {i + 1}. {s.label || getEndpoint(s.endpoint_id)?.label || s.endpoint_id}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Current step form */}
              {(() => {
                const step = selectedFlow.steps[currentStep];
                if (!step) return null;
                const ep = getEndpoint(step.endpoint_id);
                if (!ep) return <p className="text-xs text-destructive">Endpoint "{step.endpoint_id}" not found in catalog.</p>;
                return (
                  <div className="space-y-3 border-t pt-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{ep.label}</Badge>
                      {ep.description && <span className="text-[11px] text-muted-foreground">{ep.description}</span>}
                    </div>
                    {ep.params?.map((p) => {
                      const mapping = step.param_mappings?.[p.key];
                      const autoValue = mapping?.source === "step" ? resolveParamValue(mapping) : null;
                      const hasAutoFill = !!stepParams[p.key] && !autoValue;
                      return (
                        <div key={p.key}>
                          <Label className="text-xs flex items-center gap-1">{p.label}{p.required && <span className="text-destructive">*</span>}
                            {autoValue != null && <Badge variant="outline" className="text-[9px] ml-1 bg-blue-50 text-blue-600 border-blue-200">from step {(mapping?.step_index ?? 0) + 1}</Badge>}
                            {hasAutoFill && <Badge variant="outline" className="text-[9px] ml-1 bg-emerald-50 text-emerald-600 border-emerald-200">auto-filled from selection</Badge>}
                          </Label>
                          {p.type === "select" ? (
                            <Select value={stepParams[p.key] || ""} onValueChange={(v) => setStepParams({ ...stepParams, [p.key]: v })}>
                              <SelectTrigger className="mt-1"><SelectValue placeholder="Pick one…" /></SelectTrigger>
                              <SelectContent>{p.options?.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (
                            <Input type={p.type === "number" ? "number" : "text"} value={stepParams[p.key] || ""} onChange={(e) => setStepParams({ ...stepParams, [p.key]: e.target.value })} placeholder={autoValue || ""} className="mt-1 font-mono text-sm" />
                          )}
                        </div>
                      );
                    })}
                    <Button onClick={runStep} disabled={runningStep} data-testid="run-step-btn">
                      {runningStep ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                      Run step {currentStep + 1}
                    </Button>
                  </div>
                );
              })()}
            </div>

            {/* Step results */}
            {stepResults.map((r, i) => r ? (
              <div key={i} className="surface-card p-4" data-testid={`step-result-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  {r.ok ? <CheckCircle2 className="h-4 w-4 text-success" /> : <XCircle className="h-4 w-4 text-destructive" />}
                  <span className="text-sm font-medium">Step {i + 1} result</span>
                  <span className="text-xs font-mono text-muted-foreground">{r.elapsed_ms}ms</span>
                  <div className="ml-auto flex items-center gap-1">
                    {r.outbound_request && <CopyRequestMenu request={r.outbound_request} response={r.response} />}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px]"
                      onClick={() => { try { navigator.clipboard.writeText(JSON.stringify(r, null, 2)); toast.success("Copied step result"); } catch { /* ignore */ } }}
                      title="Copy entire step result (request + response)"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Step JSON
                    </Button>
                  </div>
                </div>
                {/* Outbound request */}
                {r.outbound_request && (
                  <div className="mb-3">
                    <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Request</div>
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 space-y-1.5">
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-primary/10 text-primary border border-primary/20">{r.outbound_request.method || "POST"}</span>
                        <code className="break-all flex-1 text-[11px]">{r.outbound_request.url}</code>
                      </div>
                      {r.outbound_request.headers && (
                        <details className="text-[11px]">
                          <summary className="text-muted-foreground cursor-pointer">Headers ({Object.keys(r.outbound_request.headers).length})</summary>
                          <pre className="mt-1 font-mono bg-stone-950 text-stone-100 p-2 rounded overflow-auto max-h-24 text-[10px]">{JSON.stringify(r.outbound_request.headers, null, 2)}</pre>
                        </details>
                      )}
                      {r.outbound_request.body && (
                        <div>
                          <div className="text-[10px] text-muted-foreground mb-0.5">Body:</div>
                          <pre className="font-mono bg-stone-950 text-stone-100 p-2 rounded overflow-auto max-h-32 text-[10px]">{JSON.stringify(r.outbound_request.body, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {r.error && <div className="text-xs text-destructive mb-2">{r.error}</div>}

                {/* Array response → item picker */}
                {Array.isArray(r.response) && r.response.length > 0 && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                        Response ({r.response.length} items) — pick one for next step
                      </div>
                      {selectedItems[i] && (
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          Selected: {selectedItems[i].name || selectedItems[i].id || "item"}
                        </Badge>
                      )}
                    </div>
                    {pickerStep === i ? (
                      <div className="border rounded-md overflow-hidden">
                        <div className="p-2 border-b bg-muted/50">
                          <input
                            autoFocus className="w-full bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                            placeholder="Type to search…" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
                          />
                        </div>
                        <div className="max-h-64 overflow-auto">
                          {r.response
                            .filter((item) => {
                              if (!pickerSearch) return true;
                              const s = pickerSearch.toLowerCase();
                              return JSON.stringify(item).toLowerCase().includes(s);
                            })
                            .map((item, idx) => {
                              const itemId = item.id || item.name || idx;
                              const isSelected = selectedItems[i] && (selectedItems[i].id || selectedItems[i].name) === (item.id || item.name);
                              const label = item.name || item.id || `Item ${idx + 1}`;
                              const sub = item.id && item.name ? item.id : (item.category || item.displayname || "");
                              return (
                                <div key={idx}
                                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent/50 border-b last:border-0 transition-colors ${isSelected ? "bg-emerald-50" : ""}`}
                                  onClick={() => {
                                    setSelectedItems({ ...selectedItems, [i]: item });
                                    setPickerStep(null);
                                    setPickerSearch("");
                                    toast.success(`Selected: ${label}`);
                                  }}>
                                  <div className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${isSelected ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground/30"}`} />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{label}</div>
                                    {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => { setPickerStep(i); setPickerSearch(""); }} className="w-full justify-center">
                        {selectedItems[i] ? `Change selection (current: ${selectedItems[i].name || selectedItems[i].id})` : `Pick an item from ${r.response.length} results`}
                      </Button>
                    )}
                  </div>
                )}

                <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Response</div>
                <pre className="rounded-md bg-stone-950 text-stone-100 p-3 overflow-auto max-h-48 text-[11px] font-mono">{r.response != null ? JSON.stringify(Array.isArray(r.response) && selectedItems[i] ? selectedItems[i] : r.response, null, 2) : "// no response"}</pre>
              </div>
            ) : null)}
          </>
        )}
      </div>

      {/* Save flow dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save as new flow</DialogTitle>
            <DialogDescription>Save the current step configuration as a reusable flow template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g., BBPS Credit Card Full Test" className="mt-1" /></div>
            <div><Label>Description (optional)</Label><Input value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)} placeholder="What this flow does..." className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={saveAsFlow}><Save className="h-4 w-4 mr-1" /> Save flow</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

